/**
 * Pure reducer for investigation-bind SSE — atomic UI spine swap.
 * Callers: InsightsChat.applySseEvent; apply-investigation-bind.test.ts.
 */
import type {
  InsightsChatItem,
  InsightsCoverageSnapshot,
  InsightsTaskList,
  InsightsThreadInvestigationSummary,
  InsightsToolActivity,
  InsightsWorkingFrame,
} from "../types";
import { buildHydratedItems } from "./hydrate-transcript";

export type InvestigationBindPayload = {
  investigationId: string;
  primaryQuestion: string;
  investigations?: InsightsThreadInvestigationSummary[];
  messages?: Array<{
    id?: string;
    role: string;
    content: string;
    createdAt?: string;
    investigationId?: string;
    partId?: string;
    assistantKind?: string;
  }>;
  toolActivity?: InsightsToolActivity[];
  taskList?: InsightsTaskList | null;
  coverageSnapshot?: InsightsCoverageSnapshot | null;
  workingFrame?: InsightsWorkingFrame | null;
  reportMarkdown?: string;
  runStartedAt?: string | null;
  runDurationMs?: number | null;
};

export type InvestigationBindUiState = {
  items: InsightsChatItem[];
  taskList: InsightsTaskList | null;
  hostCoverage: InsightsCoverageSnapshot | null;
  hostWorkingFrame: InsightsWorkingFrame | null;
  reportMarkdown: string;
  investigations: InsightsThreadInvestigationSummary[];
  activeInvestigationId: string | null;
  selectedInvestigationId: string | null;
  runStartedAt: string | null;
  runDurationMs: number | null;
  primaryQuestion: string;
};

function ensurePicker(
  rows: InsightsThreadInvestigationSummary[],
  id: string,
  q: string,
): InsightsThreadInvestigationSummary[] {
  if (!id || !q) return rows;
  const idx = rows.findIndex((row) => row.investigationId === id);
  if (idx >= 0) {
    if (rows[idx]!.question.trim() === q) return rows;
    const next = rows.slice();
    next[idx] = { ...next[idx]!, question: q };
    return next;
  }
  return [
    ...rows,
    {
      investigationId: id,
      question: q,
      createdAt: new Date().toISOString(),
      status: "open",
    },
  ];
}

/** Apply an investigation-bind payload into a full UI snapshot. */
export function applyInvestigationBind(
  payload: InvestigationBindPayload,
  prevInvestigations: InsightsThreadInvestigationSummary[] = [],
): InvestigationBindUiState | null {
  const investigationId = payload.investigationId?.trim();
  if (!investigationId) return null;
  const primaryQuestion = payload.primaryQuestion?.trim() || "";
  const listed = Array.isArray(payload.investigations)
    ? payload.investigations
    : prevInvestigations;
  const investigations = ensurePicker(listed, investigationId, primaryQuestion);
  const messages = Array.isArray(payload.messages) ? payload.messages : [];
  const toolActivity = Array.isArray(payload.toolActivity) ? payload.toolActivity : [];
  const taskList =
    payload.taskList && Array.isArray(payload.taskList.tasks) && payload.taskList.tasks.length > 0
      ? payload.taskList
      : null;
  const hostCoverage = payload.coverageSnapshot?.platforms?.length
    ? payload.coverageSnapshot
    : null;
  const hostWorkingFrame = payload.workingFrame?.definition ? payload.workingFrame : null;
  const reportMarkdown = typeof payload.reportMarkdown === "string" ? payload.reportMarkdown : "";

  return {
    items: buildHydratedItems(messages, toolActivity, null, undefined),
    taskList,
    hostCoverage,
    hostWorkingFrame,
    reportMarkdown,
    investigations,
    activeInvestigationId: investigationId,
    selectedInvestigationId: investigationId,
    runStartedAt: typeof payload.runStartedAt === "string" ? payload.runStartedAt : null,
    runDurationMs: typeof payload.runDurationMs === "number" ? payload.runDurationMs : null,
    primaryQuestion,
  };
}
